import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Tab, { TabProps } from "@insite/mobius/Tab";
import TabGroup from "@insite/mobius/TabGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import truncate from "lodash/truncate";
import * as React from "react";
import { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, FlattenSimpleInterpolation } from "styled-components";

const enum fields {
    showDescription = "showDescription",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showDescription]: boolean;
    };
}

type Props = WidgetProps & HasProduct & OwnProps;

export interface NISProductDetailsOverviewStyles {
    attributeTypeValuesText?: TypographyPresentationProps;
    container?: InjectableCss;
    titleStyle?: InjectableCss;
}

export const nisOverviewStyles: NISProductDetailsOverviewStyles = {
    attributeTypeValuesText: {
        css: css`
            font-family: Roboto;
            font-weight: 400;
            font-size: 14px;
            line-height: 24px;
            color: #333333;
            & * {
                font-family: inherit;
                font-size: inherit;
                line-height: inherit;
                color: inherit;
                font-weight: inherit;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        padding-right: 208px;
                    `,
                    css`
                        padding-right: 208px;
                    `,
                ])}
        `,
    },
    container: {
        css: css`
            width: 100%;
            height: 100%;
            margin: 40px 0px 48px 0px;
            display: flex !important;
            flex-direction: column !important;
            gap: 24px !important;
            padding-left: 19px;
            padding-right: 19px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 24px 0px 7px 0px;
                        padding: 0px;
                    `,
                    css`
                        margin: 24px 0px 7px 0px;
                        padding: 0px;
                    `,
                    css`
                        margin-left: -46px;
                        margin-top: 24px;
                        margin-bottom: 40px;
                        gap: 16px;
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
    titleStyle: {
        css: css`
            font-family: "Roboto Condensed";
            font-weight: 600;
            font-size: 32px;
            line-height: 40px;
            color: #2b5197;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 24px;
                        line-height: 32px;
                    `,
                    css`
                        font-size: 24px;
                        line-height: 32px;
                    `,
                    css`
                        font-size: 24px;
                        line-height: 32px;
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
};

const styles = nisOverviewStyles;

const NISProductDetailsOverview: React.FC<Props> = ({
    product: { content, properties },
    fields: { showDescription },
}) => {
    const description = content?.htmlContent ? content?.htmlContent : properties.brandHtmlContent;
    const renderingDescription = description && description.split("</head>");
    const hasValidDescription = description && description.trim() !== "";
    if (!hasValidDescription) {
        return null;
    }

    if (!hasValidDescription) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.titleStyle} as="p">
                {translate("Product Overview")}
            </Typography>
            <Typography {...styles.attributeTypeValuesText}>
                {parse(transformContentHtml(description), parserOptions)}
            </Typography>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(null)(NISProductDetailsOverview)),
    definition: {
        displayName: "NIS Product Overview",
        group: "Product Details",
    },
};

export default widgetModule;
